// Incident Labs progress (Phase 1: local practice only).
//
// The lab runtime (src/core/runtime.js) talks to a "progress" object: it reads `store`, and on a
// validated finish calls markComplete(domainId, unitId, meta). This module provides that interface
// for incidents, with its OWN record under the store key `incidents`. It never reads or writes the
// curriculum record (`progress`), so an incident cannot complete, unlock or reset a mission.
//
//   nell:v1:incidents            → { runs: { [incidentId]: { solved, lastSolvedAt, lastHints } } }
//   nell:v1:lab:inc.<incidentId> → the incident's live lab state (LabRuntime)
//
// Nothing here is server-verified, and nothing reads it for XP, quests, medals or certificates
// (they read the curriculum record and the verified completions only).
import { INCIDENT_DOMAIN, findIncident, incidentKey } from '../incidents/index.js';

export const INCIDENTS_KEY = 'incidents';
const blank = () => ({ runs: {} });

/**
 * @param st                learner store, or the admin sandbox store
 * @param opts.domainDone   (domainId) → boolean: is that curriculum domain complete? An incident
 *                          unlocks when its `incident.related` domain is complete (the same local
 *                          progress that unlocks missions). Omitted → everything is unlocked (admin
 *                          test mode, and unit tests that do not exercise unlocking).
 */
export function createIncidentProgress(st, { domainDone = null } = {}) {
  let rec = st.get(INCIDENTS_KEY) || blank();
  const save = () => st.set(INCIDENTS_KEY, rec);
  const known = (dId, id) => dId === INCIDENT_DOMAIN && !!findIncident(id);
  const labOf = (id) => st.get(`lab:${incidentKey(id)}`);
  const unlocked = (id) => {
    if (!domainDone) return true;
    const rel = findIncident(id)?.incident?.related;
    try { return !rel || !!domainDone(rel); } catch { return false; }
  };

  const api = {
    store: st,
    reload() { rec = st.get(INCIDENTS_KEY) || blank(); },

    /** Is this incident unlocked for this learner (its related domain is complete)? */
    isUnlocked(dId, id) { return known(dId, id) && unlocked(id); },

    /** 'pending' (unknown) | 'locked' | 'available' | 'in-progress' | 'complete' — of the CURRENT run. */
    unitState(dId, id) {
      if (!known(dId, id)) return 'pending';
      if (!unlocked(id)) return 'locked';
      const lab = labOf(id);
      if (lab?.completed) return 'complete';
      return lab ? 'in-progress' : 'available';
    },
    canOpen(dId, id) { return known(dId, id) && unlocked(id); },

    /** Called by LabRuntime after the last stage validates. Local record only (XP comes from the server-verified solve). */
    markComplete(dId, id, meta = {}) {
      if (!api.canOpen(dId, id)) return false;
      const r = rec.runs[id] || {};
      rec.runs[id] = { solved: (r.solved || 0) + 1, lastSolvedAt: Date.now(), lastHints: Math.max(0, Number(meta.hints) || 0), local: true, verified: false };
      save();
      return true;
    },

    /**
     * Reset ONE incident: removes its lab state so the next open starts a fresh run with a new
     * seed (and so a new rolled fault). The solved count is history and is kept.
     */
    resetUnit(dId, id) {
      if (!known(dId, id)) return;
      st.remove(`lab:${incidentKey(id)}`);
    },

    unitRecord: (dId, id) => ({ ...(known(dId, id) ? rec.runs[id] || {} : {}) }),
    raw: () => JSON.parse(JSON.stringify(rec)),
  };
  return api;
}
